import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAccountMfaSetup,
  clearAccountMessages,
  fetchAccountSettingsUserThunk,
  saveAccountSettingsThunk,
  selectAdminState,
  setupAccountMfaThunk,
  verifyAccountMfaThunk,
} from "../store/adminSlice";

const getSafeTimezone = (timezone, options) =>
  options.includes(timezone) ? timezone : "UTC";

const isSixDigitCode = (value) => /^\d{6}$/.test(String(value || ""));

export const useAccountSettings = () => {
  const dispatch = useDispatch();
  const adminState = useSelector(selectAdminState);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [timeZone, setTimeZone] = useState("UTC");
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaCodeVerified, setMfaCodeVerified] = useState(false);
  const [mfaVerificationError, setMfaVerificationError] = useState("");
  const [mfaSetupStep, setMfaSetupStep] = useState("done");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    dispatch(fetchAccountSettingsUserThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!adminState.accountUser) return;

    setTimeZone(
      getSafeTimezone(adminState.accountUser.timezone, adminState.timezoneOptions),
    );
    setMfaEnabled(Boolean(adminState.accountUser.two_fa_enabled));
    setMfaCode("");
    setMfaCodeVerified(false);
    setMfaVerificationError("");
    setMfaSetupStep("done");
  }, [adminState.accountUser, adminState.timezoneOptions]);

  useEffect(() => {
    const message = adminState.accountError || adminState.accountSuccessMessage;
    if (!message) return;

    setSnackbar({
      open: true,
      message,
      severity: adminState.accountError ? "error" : "success",
    });

    const timeoutId = setTimeout(() => {
      dispatch(clearAccountMessages());
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [adminState.accountError, adminState.accountSuccessMessage, dispatch]);

  const handleMfaToggleChange = useCallback(
    async (event) => {
      const nextEnabled = event.target.checked;
      setMfaVerificationError("");

      if (!nextEnabled) {
        dispatch(clearAccountMfaSetup());
        setMfaEnabled(false);
        setMfaCode("");
        setMfaCodeVerified(false);
        setMfaSetupStep("done");
        return;
      }

      setMfaEnabled(true);
      setMfaCode("");
      setMfaCodeVerified(false);
      setMfaSetupStep("scan");

      const action = await dispatch(setupAccountMfaThunk(adminState.accountUser?.email));

      if (!setupAccountMfaThunk.fulfilled.match(action)) {
        setMfaEnabled(false);
        setMfaVerificationError(action.payload || "Unable to set up authenticator.");
      }
    },
    [adminState.accountUser?.email, dispatch],
  );

  const handleVerifyMfaCode = useCallback(async () => {
    if (!isSixDigitCode(mfaCode)) {
      setMfaVerificationError("Enter a valid 6-digit code.");
      return;
    }

    const secret = adminState.mfaSetup?.secret;
    if (!secret) {
      setMfaVerificationError("MFA setup expired. Please restart setup.");
      return;
    }

    const action = await dispatch(
      verifyAccountMfaThunk({
        email: adminState.accountUser?.email,
        token: mfaCode,
        secret,
      }),
    );

    if (!verifyAccountMfaThunk.fulfilled.match(action)) {
      setMfaCodeVerified(false);
      setMfaVerificationError(action.payload || "Invalid authenticator code.");
      return;
    }

    setMfaCodeVerified(true);
    setMfaVerificationError("");

    const persistAction = await dispatch(
      saveAccountSettingsThunk({
        user: adminState.accountUser,
        timezone: timeZone,
        mfaEnabled: true,
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
        mfaCodeVerified: true,
      }),
    );

    if (!saveAccountSettingsThunk.fulfilled.match(persistAction)) {
      setMfaCodeVerified(false);
      setMfaVerificationError(
        persistAction.payload || "Authenticator verified, but enabling MFA failed. Please try again.",
      );
      return;
    }

    setMfaSetupStep("success");
    setSnackbar({
      open: true,
      message: "Authenticator verified successfully.",
      severity: "success",
    });
  }, [
    adminState.accountUser,
    adminState.accountUser?.email,
    adminState.mfaSetup?.secret,
    dispatch,
    mfaCode,
    timeZone,
  ]);

  const handleContinueMfaSetup = useCallback(() => {
    setMfaSetupStep("verify");
  }, []);

  const handleCloseMfaSetupFlow = useCallback(() => {
    const persistedMfaEnabled = Boolean(adminState.accountUser?.two_fa_enabled);

    // Reset UI state to persisted backend truth when setup is dismissed midway.
    setMfaEnabled(persistedMfaEnabled);
    setMfaCode("");
    setMfaCodeVerified(false);
    setMfaVerificationError("");
    setMfaSetupStep("done");
    dispatch(clearAccountMfaSetup());
  }, [adminState.accountUser?.two_fa_enabled, dispatch]);

  const handleMfaCodeChange = useCallback((eventOrValue) => {
    const rawValue =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value;

    const nextValue = String(rawValue || "")
      .replace(/\D/g, "")
      .slice(0, 6);
    setMfaCode(nextValue);
    setMfaCodeVerified(false);
    setMfaVerificationError("");
  }, []);

  const hasPasswordInput = Boolean(oldPassword || newPassword || confirmPassword);
  const isPasswordInputComplete = Boolean(oldPassword && newPassword && confirmPassword);
  const isTimezoneChanged =
    Boolean(adminState.accountUser) &&
    timeZone !== getSafeTimezone(adminState.accountUser?.timezone, adminState.timezoneOptions);
  const isMfaChanged =
    Boolean(adminState.accountUser) &&
    mfaEnabled !== Boolean(adminState.accountUser?.two_fa_enabled);
  const isMfaDisablePendingSave = isMfaChanged && !mfaEnabled;
  const isMfaEnablePendingVerification = isMfaChanged && mfaEnabled && !mfaCodeVerified;

  const hasAnyMeaningfulChange =
    isTimezoneChanged ||
    isMfaDisablePendingSave ||
    hasPasswordInput;

  const canSaveChanges = useMemo(
    () =>
      Boolean(adminState.accountUser) &&
      !adminState.isAccountSaving &&
      !adminState.isSettingUpMfa &&
      !adminState.isVerifyingMfa &&
      hasAnyMeaningfulChange &&
      (!hasPasswordInput || isPasswordInputComplete) &&
      !isMfaEnablePendingVerification,
    [
      adminState.accountUser,
      adminState.isAccountSaving,
      adminState.isSettingUpMfa,
      adminState.isVerifyingMfa,
      hasAnyMeaningfulChange,
      hasPasswordInput,
      isMfaEnablePendingVerification,
      isPasswordInputComplete,
    ],
  );

  const handleSaveChanges = useCallback(async () => {
    const action = await dispatch(
      saveAccountSettingsThunk({
        user: adminState.accountUser,
        timezone: timeZone,
        mfaEnabled,
        oldPassword,
        newPassword,
        confirmPassword,
        mfaCodeVerified,
      }),
    );

    if (saveAccountSettingsThunk.fulfilled.match(action)) {
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  }, [
    adminState.accountUser,
    confirmPassword,
    dispatch,
    mfaCodeVerified,
    mfaEnabled,
    newPassword,
    oldPassword,
    timeZone,
  ]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    currentUser: adminState.accountUser,
    loadError: adminState.accountLoadError,
    loading: adminState.isAccountSaving,
    isSettingUpMfa: adminState.isSettingUpMfa,
    isVerifyingMfa: adminState.isVerifyingMfa,
    mfaSetup: adminState.mfaSetup,
    oldPassword,
    newPassword,
    confirmPassword,
    timeZone,
    mfaEnabled,
    mfaCode,
    mfaCodeVerified,
    mfaVerificationError,
    mfaSetupStep,
    showOldPassword,
    showNewPassword,
    showConfirmPassword,
    snackbar,
    timezoneOptions: adminState.timezoneOptions,
    isMfaChanged,
    canSaveChanges,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    setTimeZone,
    setShowOldPassword,
    setShowNewPassword,
    setShowConfirmPassword,
    setMfaSetupStep,
    handleMfaToggleChange,
    handleVerifyMfaCode,
    handleContinueMfaSetup,
    handleCloseMfaSetupFlow,
    handleMfaCodeChange,
    handleSaveChanges,
    handleCloseSnackbar,
  };
};

export default useAccountSettings;
